// File: AssignTeamMember.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Input,
  Label,
  Row,
  Spinner,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import swal from "sweetalert";
import { useNavigate } from "react-router-dom";

import { _Get, _PostSave } from "../import/ApiEndPoint/ApiCalling";
import {
  All_Users_List, // /user/user-list/
  Deptartment_with_Role, // /assign-role/view-assign-role/
  User_Assign_User, // /user/assign-user
  User_UnLink_User, // /user/delete-assign-user
} from "../import/ApiEndPoint/Api";

/* ============================== */
/* Helpers                        */
/* ============================== */
const norm = (s) => (s || "").toString().trim().toLowerCase();
const compact = (s) => norm(s).replace(/[\s_\-./]+/g, "");

const getRoleName = (u) => {
  const direct =
    u?.rolename?.roleName ||
    u?.roleName ||
    u?.role?.roleName ||
    u?.role?.name ||
    u?.designation ||
    "";

  if (direct) return direct;

  if (typeof u?.rolename === "string") {
    const raw = u.rolename.trim();
    // A raw Mongo ObjectId is a role id, not a role name.
    if (/^[a-f0-9]{24}$/i.test(raw)) return "";
    return raw;
  }

  return "";
};

const getUserRoleId = (u) => {
  const raw = u?.rolename;
  if (raw && typeof raw === "object" && raw?._id) return String(raw._id);
  if (typeof raw === "string" && /^[a-f0-9]{24}$/i.test(raw.trim())) {
    return raw.trim();
  }

  const other = u?.roleId?._id || u?.roleId || u?.role?._id;
  return other ? String(other) : "";
};

const isSalesPersonRole = (roleName) =>
  compact(roleName).includes("salesperson");

const isActiveUser = (u) => {
  const status = norm(u?.status);
  // Some customer records do not carry a status field. Treat only explicit
  // inactive/disabled values as inactive so valid customers are not hidden.
  if (!status) return u?.active !== false && u?.isActive !== false;
  return !["inactive", "disabled", "deleted", "blocked"].includes(status);
};

const isCustomerUser = (u) => {
  const roleKey = compact(getRoleName(u));

  // Flexible role matching fixes Customer User / Party User / Client etc.
  if (
    roleKey.includes("customer") ||
    roleKey.includes("party") ||
    roleKey.includes("client") ||
    roleKey.includes("dealer") ||
    roleKey.includes("retailer") ||
    roleKey.includes("outlet")
  ) {
    return true;
  }

  // Support APIs that identify customer records by type/flags instead of
  // a populated rolename object.
  const typeKey = compact(
    u?.userType || u?.accountType || u?.entityType || u?.partyType || u?.type,
  );
  if (
    typeKey.includes("customer") ||
    typeKey.includes("party") ||
    typeKey.includes("client") ||
    typeKey.includes("dealer") ||
    typeKey.includes("retailer") ||
    typeKey.includes("outlet")
  ) {
    return true;
  }

  if (
    u?.isCustomer === true ||
    u?.isParty === true ||
    u?.customer === true ||
    u?.party === true
  ) {
    return true;
  }

  // Legacy APIs sometimes return customer records without a populated role.
  // Only use this shape fallback when the role is empty/generic so employees
  // with a real department role are never mistaken for customers.
  const genericRole = !roleKey || ["user", "users", "member"].includes(roleKey);
  const hasBusinessName = Boolean(
    u?.CompanyName ||
    u?.companyName ||
    u?.customerName ||
    u?.partyName ||
    u?.clientName,
  );
  const hasCustomerContact = Boolean(
    u?.pincode ||
    u?.address ||
    u?.mobileNumber ||
    u?.mobile ||
    u?.phone ||
    u?.GSTIN ||
    u?.gstin,
  );

  return genericRole && hasBusinessName && hasCustomerContact;
};

const displayName = (u) => {
  const full = [u?.firstName, u?.lastName].filter(Boolean).join(" ").trim();
  return (
    u?.CompanyName ||
    u?.companyName ||
    u?.fullName ||
    full ||
    u?.name ||
    u?.contactName ||
    u?.username ||
    u?.UserName ||
    u?.email ||
    "Unnamed"
  );
};

const displayAddress = (u) => {
  const parts = [
    u?.address,
    u?.City && `City: ${u.City}`,
    u?.State && `State: ${u.State}`,
  ]
    .filter(Boolean)
    .join(", ");
  return parts || "-";
};

// ✅ created_by can be OBJECT or just ID string
const getCreatedByUser = (created_by, usersOrMap = []) => {
  if (!created_by) return null;

  // Case 1: already populated object
  if (typeof created_by === "object") return created_by;

  // Case 2: O(1) lookup when a Map is supplied (used by large tables)
  if (usersOrMap instanceof Map) {
    return usersOrMap.get(String(created_by)) || null;
  }

  // Compatibility fallback
  return (
    (usersOrMap || []).find((u) => String(u?._id) === String(created_by)) ||
    null
  );
};

const hasParentAssigned = (u) => {
  if (!u?.created_by) return false;
  if (typeof u.created_by === "object") return !!u.created_by?._id;
  return !!u.created_by;
};

const CHILD_RENDER_BATCH = 100;

/* ============================== */
/* SalesPersonPincodes page logic */
/* ============================== */
const uniqByPincode = (arr) => {
  const seen = new Set();
  const out = [];
  for (const x of arr || []) {
    const key = String(x?.pincode || "").trim();
    if (!key) continue;
    if (!seen.has(key)) {
      seen.add(key);
      out.push(x);
    }
  }
  return out;
};

const salesPersonPincodeSet = (sp) => {
  const svc = Array.isArray(sp?.service) ? sp.service : [];
  const unique = uniqByPincode(svc);
  return new Set(
    unique.map((r) => String(r?.pincode || "").trim()).filter(Boolean),
  );
};

/* ============================== */
/* Minimal CSS (no hardcode roles) */
/* ============================== */
const injectOnce = () => {
  if (document.getElementById("assign-team-inline-css")) return;
  const s = document.createElement("style");
  s.id = "assign-team-inline-css";
  s.textContent = `
    .ht-wrap{ border:1px solid #eef2f7; border-radius:12px; overflow:hidden; background:#fff; }
    .ht-head{ background:#f8fafc; border-bottom:1px solid #eef2f7; padding:12px; }
    .ht-table thead th{ background:#f8fafc; position:sticky; top:0; z-index:2; }
    .ht-table td, .ht-table th{ vertical-align:middle !important; }
    .ht-pill{ background:#eef2ff; color:#3730a3; padding:6px 10px; border-radius:999px; font-weight:700; font-size:12px; }
    .ht-muted{ color:#64748b; font-size:12px; }
    .ht-actions{ display:flex; gap:12px; align-items:center; }
    .ht-check{ display:flex; gap:6px; align-items:center; white-space:nowrap; }
    .ht-select{ min-width:170px; }
  `;
  document.head.appendChild(s);
};

const AssignTeamMember = () => {
  // ------- Data -------
  const navigate = useNavigate();
  const [departments, setDepartments] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);

  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false); // manual assign/unlink
  const [autoWorking, setAutoWorking] = useState(false); // auto-assign flow

  // ------- Selections / UI state -------
  const [selectedDeptId, setSelectedDeptId] = useState("");
  const [selectedDeptRoles, setSelectedDeptRoles] = useState([]);
  const [showParentUI, setShowParentUI] = useState(false);

  const [selectedParentRoleOpt, setSelectedParentRoleOpt] = useState("NA");
  const [parentRoleName, setParentRoleName] = useState("");
  const [parentRoleId, setParentRoleId] = useState("");
  const [parentRolePosition, setParentRolePosition] = useState(null);

  const [selectedParentUser, setSelectedParentUser] = useState(null);
  const [parentNameDropdownValue, setParentNameDropdownValue] = useState("");

  const [selectedChildren, setSelectedChildren] = useState(new Set());
  const [selectedChildrenForUnlink, setSelectedChildrenForUnlink] = useState(
    new Set(),
  );

  const [parentSearch, setParentSearch] = useState("");
  const [childSearch, setChildSearch] = useState("");
  const [visibleChildCount, setVisibleChildCount] =
    useState(CHILD_RENDER_BATCH);

  const [partyMode, setPartyMode] = useState(false);
  const [noNextChild, setNoNextChild] = useState(false);

  // Auto-assign conflict dialog
  const [autoModalOpen, setAutoModalOpen] = useState(false);
  const [autoRows, setAutoRows] = useState([]);

  // ==============================
  // NEW: Department hierarchy table UI state
  // ==============================
  const [hierSearch, setHierSearch] = useState("");
  // selections for dropdowns in missing cells:
  // rowPick[custId][roleId] = selectedUserId
  const [rowPick, setRowPick] = useState({});
  // Link/Unlink checkboxes per customer row
  const [rowLink, setRowLink] = useState(new Set());
  const [rowUnlink, setRowUnlink] = useState(new Set());

  // ------- Fetch data -------
  useEffect(() => {
    injectOnce();

    let cancelled = false;
    let maintenanceTimer = null;

    const loadPage = async () => {
      try {
        setLoading(true);
        const userData = JSON.parse(localStorage.getItem("userData") || "{}");
        setCurrentUser(userData);

        // Only the two required reads block the first screen.
        const [usersRes, deptRes] = await Promise.all([
          _Get(All_Users_List, userData?.database),
          _Get(Deptartment_with_Role, userData?.database),
        ]);

        const users = Array.isArray(usersRes?.User) ? usersRes.User : [];
        const deptArr = Array.isArray(deptRes?.Department)
          ? deptRes.Department
          : [];

        if (cancelled) return;

        // Render immediately. The old page waited for maintenance writes and
        // extra refetches here, which was the biggest page-load delay.
        setAllUsers(users);
        setDepartments(deptArr);
        setLoading(false);

        // Keep the existing data-repair behaviour, but do it AFTER the page is
        // usable and run the independent checks in parallel. Refresh only once.
        maintenanceTimer = window.setTimeout(async () => {
          try {
            const [autoUnassignedCount, hierarchyFixedCount] =
              await Promise.all([
                autoUnassignOnLoad(users),
                autoFixHierarchyOnLoad(users, deptArr),
              ]);

            if (cancelled) return;

            if (autoUnassignedCount > 0 || hierarchyFixedCount > 0) {
              const refreshedUsersRes = await _Get(
                All_Users_List,
                userData?.database,
              );

              if (!cancelled && Array.isArray(refreshedUsersRes?.User)) {
                setAllUsers(refreshedUsersRes.User);
              }
            }

            if (!cancelled && hierarchyFixedCount > 0) {
              swal(
                "Hierarchy Fixed",
                `${hierarchyFixedCount} assignment(s) were moved to the correct higher-level role as per updated hierarchy.`,
                "success",
              );
            }
          } catch (maintenanceError) {
            // Maintenance must never block or break the page.
            console.error(
              "[AssignTeamMember] background maintenance failed:",
              maintenanceError,
            );
          }
        }, 100);
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setLoading(false);
          swal("Error", "Failed to load data", "error");
        }
      }
    };

    loadPage();

    return () => {
      cancelled = true;
      if (maintenanceTimer) window.clearTimeout(maintenanceTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ------- Helpers -------
  const activeUsers = useMemo(
    () => allUsers.filter((u) => isActiveUser(u)),
    [allUsers],
  );

  // O(1) id lookup. This removes the old allUsers.find(...) call from every
  // rendered row, which becomes very expensive when there are many customers.
  const userById = useMemo(() => {
    const map = new Map();
    allUsers.forEach((u) => {
      if (u?._id) map.set(String(u._id), u);
    });
    return map;
  }, [allUsers]);

  // Used only as a safe fallback for legacy customer records whose rolename is
  // returned as a raw role id instead of a populated role object.
  const departmentRoleIds = useMemo(() => {
    const ids = new Set();
    departments.forEach((department) => {
      (department?.roles || []).forEach((role) => {
        const id = role?.roleId?._id || role?.roleId;
        if (id) ids.add(String(id));
      });
    });
    return ids;
  }, [departments]);

  const handleSelectDept = (value) => {
    setSelectedDeptId(value);
    setSelectedDeptRoles([]);
    setShowParentUI(!!value && value !== "0" && value !== "All_Dept_Heads");

    setSelectedParentRoleOpt("NA");
    setParentRoleName("");
    setParentRoleId("");
    setParentRolePosition(null);

    setSelectedParentUser(null);
    setParentNameDropdownValue("");

    setSelectedChildren(new Set());
    setSelectedChildrenForUnlink(new Set());

    setPartyMode(false);
    setNoNextChild(false);
    setParentSearch("");
    setChildSearch("");

    // NEW: reset hierarchy table selections
    setHierSearch("");
    setRowPick({});
    setRowLink(new Set());
    setRowUnlink(new Set());

    if (value && value !== "0" && value !== "All_Dept_Heads") {
      const found = departments.find((d) => d?._id === value);
      setSelectedDeptRoles(found?.roles || []);
    }
  };

  // Parent users list from selected parent role
  const parentUsers = useMemo(() => {
    if (selectedDeptId === "All_Dept_Heads") {
      return currentUser ? [currentUser] : [];
    }
    if (!parentRoleId) return [];
    return activeUsers.filter(
      (u) => String(getUserRoleId(u)) === String(parentRoleId),
    );
  }, [selectedDeptId, currentUser, activeUsers, parentRoleId]);

  const filteredParentUsers = useMemo(() => {
    if (!parentSearch) return parentUsers;
    const q = parentSearch.toLowerCase();
    return parentUsers.filter((u) => displayName(u).toLowerCase().includes(q));
  }, [parentUsers, parentSearch]);

  // show selected parent row (dropdown), else first row
  const parentRowUser = useMemo(() => {
    if (selectedParentUser?._id) return selectedParentUser;
    return filteredParentUsers?.[0] || null;
  }, [selectedParentUser, filteredParentUsers]);

  // Child role (next position) when a parent role is selected
  const childRoleCandidates = useMemo(() => {
    if (selectedDeptId === "All_Dept_Heads") return null;
    if (!selectedDeptRoles?.length || parentRolePosition == null) return [];
    const nextPos = Number(parentRolePosition) + 1;
    return selectedDeptRoles.filter((r) => r?.rolePosition === nextPos);
  }, [selectedDeptId, selectedDeptRoles, parentRolePosition]);

  // IMPORTANT RULE:
  // If the selected PARENT ROLE is Sales Person, ALWAYS show customers.
  // Resolve this directly from the selected department-role object instead of
  // depending only on parsed option text, so hierarchy changes cannot break it.
  const selectedParentRoleMeta = useMemo(() => {
    if (!selectedParentRoleOpt || selectedParentRoleOpt === "NA") return null;
    return (
      (selectedDeptRoles || []).find(
        (r) => String(r?._id || "") === String(selectedParentRoleOpt),
      ) || null
    );
  }, [selectedDeptRoles, selectedParentRoleOpt]);

  const selectedParentIsSalesPerson = useMemo(
    () =>
      isSalesPersonRole(selectedParentRoleMeta?.roleName) ||
      isSalesPersonRole(parentRoleName),
    [selectedParentRoleMeta, parentRoleName],
  );

  // Child users list + customer-mode flag
  const { childUsers, isCustomerMode } = useMemo(() => {
    if (selectedDeptId === "All_Dept_Heads") {
      const headRoleIds = new Set(
        (departments || [])
          .flatMap((d) => (d?.roles || []).filter((r) => r?.rolePosition === 1))
          .map((r) => r?.roleId?._id || r?.roleId)
          .filter(Boolean)
          .map(String),
      );

      const users = allUsers
        .filter((u) => headRoleIds.has(String(getUserRoleId(u))))
        .reverse();

      return { childUsers: users, isCustomerMode: false };
    }

    // RULE:
    // 1) Sales Person selected as parent => ALWAYS show the customer list.
    //    Do not follow the next hierarchy role in this case.
    // 2) Existing fallback remains: if there is no next role, show customers.
    const noNextRole =
      !(childRoleCandidates && childRoleCandidates.length) && !!parentRoleId;

    if (selectedParentIsSalesPerson || noNextRole) {
      const explicitCustomers = allUsers.filter(
        (u) => isCustomerUser(u) && isActiveUser(u),
      );

      // Legacy fallback: some versions of /user/user-list return customer
      // rolename as only an ObjectId. In that case identify customer-shaped
      // records that are NOT members of any configured department role.
      const legacyCustomers = allUsers.filter((u) => {
        if (!isActiveUser(u)) return false;
        const roleId = getUserRoleId(u);
        if (roleId && departmentRoleIds.has(roleId)) return false;

        const hasBusinessName = Boolean(
          u?.CompanyName ||
          u?.companyName ||
          u?.customerName ||
          u?.partyName ||
          u?.clientName,
        );
        const hasContact = Boolean(
          u?.pincode ||
          u?.address ||
          u?.mobileNumber ||
          u?.mobile ||
          u?.phone ||
          u?.GSTIN ||
          u?.gstin,
        );

        return hasBusinessName && hasContact;
      });

      const users = [...explicitCustomers, ...legacyCustomers];
      const uniqueById = Array.from(
        new Map(
          users.filter((u) => u?._id).map((u) => [String(u._id), u]),
        ).values(),
      ).reverse();

      return { childUsers: uniqueById, isCustomerMode: true };
    }

    const nextRoleId = childRoleCandidates?.[0]?.roleId?._id;
    if (!nextRoleId) return { childUsers: [], isCustomerMode: false };

    const users = activeUsers
      .filter((u) => String(getUserRoleId(u)) === String(nextRoleId))
      .reverse();

    return { childUsers: users, isCustomerMode: false };
  }, [
    selectedDeptId,
    departments,
    allUsers,
    activeUsers,
    parentRoleName,
    selectedParentIsSalesPerson,
    childRoleCandidates,
    parentRoleId,
    departmentRoleIds,
  ]);

  useEffect(() => {
    setPartyMode(isCustomerMode);
    setNoNextChild(childUsers.length === 0);
  }, [childUsers, isCustomerMode]);

  const filteredChildUsers = useMemo(() => {
    if (!childSearch) return childUsers;
    const q = norm(childSearch);
    return childUsers.filter(
      (u) =>
        norm(displayName(u)).includes(q) ||
        String(u?.pincode || "").includes(q) ||
        norm(u?.address).includes(q) ||
        norm(u?.City).includes(q) ||
        norm(u?.State).includes(q),
    );
  }, [childUsers, childSearch]);

  // Rendering thousands of table rows at once freezes the browser. Show the
  // first batch immediately and let the user progressively reveal more.
  useEffect(() => {
    setVisibleChildCount(CHILD_RENDER_BATCH);
  }, [selectedDeptId, parentRoleId, childSearch]);

  const visibleChildUsers = useMemo(
    () => filteredChildUsers.slice(0, visibleChildCount),
    [filteredChildUsers, visibleChildCount],
  );

  // ------- Handlers -------
  const handleParentRoleChange = (e) => {
    const value = e.target.value;

    setSelectedParentRoleOpt(value);
    setSelectedParentUser(null);
    setParentNameDropdownValue("");
    setSelectedChildren(new Set());
    setSelectedChildrenForUnlink(new Set());
    setChildSearch("");

    if (value === "NA") {
      setParentRoleName("");
      setParentRoleId("");
      setParentRolePosition(null);
      return;
    }

    // Resolve the selected role directly from department data. This is more
    // reliable than parsing data-name and guarantees Sales Person detection.
    const selectedRole = (selectedDeptRoles || []).find(
      (r) => String(r?._id || "") === String(value),
    );

    if (selectedRole) {
      setParentRolePosition(Number(selectedRole?.rolePosition));
      setParentRoleId(
        String(selectedRole?.roleId?._id || selectedRole?.roleId || ""),
      );
      setParentRoleName(selectedRole?.roleName || "");
      return;
    }

    // Compatibility fallback for older department API response shapes.
    const opt = e.target.options[e.target.selectedIndex];
    const meta = (opt.getAttribute("data-name") || "").split(" ");
    const pos = Number(meta[0]);
    const roleId = meta[1];
    const roleName = meta.slice(2).join(" ");

    setParentRolePosition(pos);
    setParentRoleId(roleId || "");
    setParentRoleName(roleName || "");
  };

  const handlePickParentUser = (e) => {
    const id = e.target.value;
    setParentNameDropdownValue(id);

    const found = parentUsers.find((u) => String(u?._id) === String(id));
    setSelectedParentUser(found || null);
  };

  const toggleChildSelect = (user, checked) => {
    setSelectedChildren((prev) => {
      const next = new Set(prev);
      if (checked) next.add(user._id);
      else next.delete(user._id);
      return next;
    });
  };

  const toggleChildUnlink = (user, checked) => {
    setSelectedChildrenForUnlink((prev) => {
      const next = new Set(prev);
      if (checked) next.add(user._id);
      else next.delete(user._id);
      return next;
    });
  };

  const refreshUsers = async () => {
    const userData = JSON.parse(localStorage.getItem("userData") || "{}");
    const usersRes = await _Get(All_Users_List, userData?.database);
    setAllUsers(Array.isArray(usersRes?.User) ? usersRes.User : []);
  };

  const handleAssign = async () => {
    if (!selectedParentUser?._id) {
      swal("Select Parent", "Please choose a parent user first.", "warning");
      return;
    }
    if (selectedChildren.size === 0) {
      swal("Select Children", "Pick at least one child to link.", "warning");
      return;
    }

    const payload = {
      parentId: selectedParentUser._id,
      childs: Array.from(selectedChildren).map((id) => ({ id })),
    };

    try {
      setWorking(true);
      await _PostSave(User_Assign_User, payload);
      swal("Assigned Successfully");
      await refreshUsers();
      setSelectedChildren(new Set());
    } catch (err) {
      console.error(err);
      swal("Error", "Something went wrong while assigning.", "error");
    } finally {
      setWorking(false);
    }
  };

  const handleUnlink = async () => {
    if (selectedChildrenForUnlink.size === 0) {
      swal("Select Children", "Pick at least one child to unlink.", "warning");
      return;
    }

    const payload = {
      childs: Array.from(selectedChildrenForUnlink).map((id) => ({ id })),
    };

    try {
      setWorking(true);
      await _PostSave(User_UnLink_User, payload);
      swal("Unlinked Successfully");
      await refreshUsers();
      setSelectedChildrenForUnlink(new Set());
    } catch (err) {
      console.error(err);
      swal("Error", "Something went wrong while unlinking.", "error");
    } finally {
      setWorking(false);
    }
  };

  /* ==============================
     AUTO ASSIGN BY PINCODE (same)
     ============================== */
  const handleAutoAssign = async () => {
    if (!partyMode) {
      swal(
        "Auto Assign",
        "Auto Assign works only when child list is Party/Customer.",
        "info",
      );
      return;
    }

    try {
      setAutoWorking(true);

      const userData = JSON.parse(localStorage.getItem("userData") || "{}");
      const usersRes = await _Get(All_Users_List, userData?.database);
      const users = Array.isArray(usersRes?.User) ? usersRes.User : allUsers;
      setAllUsers(users);

      const active = users.filter((u) => isActiveUser(u));

      // NOTE: this is your existing auto-assign by pincode behavior;
      // it relies on "sales person" roleName in your system.
      const salesPersons = active.filter((u) =>
        isSalesPersonRole(getRoleName(u)),
      );

      if (!salesPersons.length) {
        swal(
          "Auto Assign",
          "No active Sales Persons found for auto assign.",
          "info",
        );
        return;
      }

      // Build map: pincode -> [salesPersons] (unique service list)
      const pincodeToSales = new Map();
      salesPersons.forEach((sp) => {
        const uniqueSvc = uniqByPincode(
          Array.isArray(sp?.service) ? sp.service : [],
        );
        uniqueSvc.forEach((srv) => {
          const pin = String(srv?.pincode || "").trim();
          if (!pin) return;
          const arr = pincodeToSales.get(pin) || [];
          arr.push(sp);
          pincodeToSales.set(pin, arr);
        });
      });

      if (!pincodeToSales.size) {
        swal(
          "Auto Assign",
          "No pincodes configured for any Sales Person.",
          "info",
        );
        return;
      }

      const customers = users.filter(
        (u) => isCustomerUser(u) && isActiveUser(u),
      );

      const unassignedCustomers = customers.filter(
        (c) => !hasParentAssigned(c),
      );

      if (!unassignedCustomers.length) {
        swal("Auto Assign", "No unassigned customers found.", "info");
        return;
      }

      const directAssignments = new Map(); // parentId -> Set(childId)
      const ambRows = []; // multiple sales persons for same pincode

      unassignedCustomers.forEach((cust) => {
        const pin = String(cust?.pincode || "").trim();
        if (!pin) return;
        const candidates = pincodeToSales.get(pin) || [];
        if (!candidates.length) return;

        if (candidates.length === 1) {
          const parentId = candidates[0]._id;
          if (!directAssignments.has(parentId))
            directAssignments.set(parentId, new Set());
          directAssignments.get(parentId).add(cust._id);
        } else {
          ambRows.push({
            customerId: cust._id,
            customerName: displayName(cust),
            pincode: pin,
            options: candidates,
            selectedSalesId: "",
          });
        }
      });

      let assignedCount = 0;

      for (const [parentId, childSet] of directAssignments.entries()) {
        const ids = Array.from(childSet);
        if (!ids.length) continue;

        const payload = {
          parentId,
          childs: ids.map((id) => ({ id })),
        };

        try {
          await _PostSave(User_Assign_User, payload);
          assignedCount += ids.length;
        } catch (err) {
          console.error("Auto assign direct error", parentId, err);
        }
      }

      if (assignedCount > 0) {
        swal(
          "Auto Assign",
          `Automatically assigned ${assignedCount} customer(s) to Sales Persons by pincode.`,
          "success",
        );
      } else {
        swal(
          "Auto Assign",
          "No matching pincodes found for unassigned customers.",
          "info",
        );
      }

      if (ambRows.length > 0) {
        setAutoRows(ambRows);
        setAutoModalOpen(true);
      } else {
        setAutoRows([]);
        setAutoModalOpen(false);
      }

      await refreshUsers();
    } catch (err) {
      console.error(err);
      swal("Error", "Auto assign failed.", "error");
    } finally {
      setAutoWorking(false);
    }
  };

  const handleAutoRowSalesChange = (index, salesId) => {
    setAutoRows((prev) =>
      prev.map((row, i) =>
        i === index ? { ...row, selectedSalesId: salesId } : row,
      ),
    );
  };

  const handleAutoModalSubmit = async () => {
    if (!autoRows.length) {
      setAutoModalOpen(false);
      return;
    }

    const missing = autoRows.filter((r) => !r.selectedSalesId);
    if (missing.length) {
      swal(
        "Select Sales Person",
        "Please select Sales Person for all customers in the list.",
        "warning",
      );
      return;
    }

    const grouped = new Map(); // selectedSalesId -> Set(customerId)
    autoRows.forEach((row) => {
      const parentId = row.selectedSalesId;
      if (!grouped.has(parentId)) grouped.set(parentId, new Set());
      grouped.get(parentId).add(row.customerId);
    });

    try {
      setAutoWorking(true);
      for (const [parentId, childSet] of grouped.entries()) {
        const ids = Array.from(childSet);
        if (!ids.length) continue;
        const payload = {
          parentId,
          childs: ids.map((id) => ({ id })),
        };
        await _PostSave(User_Assign_User, payload);
      }
      swal(
        "Auto Assign",
        "Selected customers have been assigned successfully.",
        "success",
      );
      setAutoModalOpen(false);
      setAutoRows([]);
      await refreshUsers();
    } catch (err) {
      console.error(err);
      swal(
        "Error",
        "Something went wrong while saving from auto-assign dialog.",
        "error",
      );
    } finally {
      setAutoWorking(false);
    }
  };

  /* ==============================
     AUTO UNASSIGN ON PAGE LOAD
     - If assigned Sales Person does NOT have customer's pincode => auto unlink customer
     ============================== */
  async function autoUnassignOnLoad(users) {
    try {
      const active = Array.isArray(users)
        ? users.filter((u) => isActiveUser(u))
        : [];
      if (!active.length) return 0;

      const salesPersons = active.filter((u) =>
        isSalesPersonRole(getRoleName(u)),
      );
      if (!salesPersons.length) return 0;

      const salesMap = new Map(
        salesPersons.map((sp) => [
          String(sp?._id),
          { sp, pins: salesPersonPincodeSet(sp) },
        ]),
      );

      const customers = active.filter((u) => isCustomerUser(u));

      const toUnlinkIds = [];

      customers.forEach((cust) => {
        const parentId =
          typeof cust?.created_by === "object"
            ? cust?.created_by?._id
            : cust?.created_by;

        const pin = String(cust?.pincode || "").trim();
        if (!parentId || !pin) return;

        const entry = salesMap.get(String(parentId));
        if (!entry) return; // parent not an active Sales Person

        if (!entry.pins.has(pin)) {
          toUnlinkIds.push(cust._id);
        }
      });

      if (!toUnlinkIds.length) return 0;

      const payload = {
        childs: toUnlinkIds.map((id) => ({ id })),
      };

      await _PostSave(User_UnLink_User, payload);
      return toUnlinkIds.length;
    } catch (err) {
      console.error("[AssignTeamMember] auto-unassign on load failed:", err);
      return 0;
    }
  }

  /* ==========================================================
     ✅ FIX: AUTO RE-LINK USERS WHEN HIERARCHY CHANGES
  ========================================================== */
  async function autoFixHierarchyOnLoad(users, deptArr) {
    try {
      if (!Array.isArray(users) || !Array.isArray(deptArr)) return 0;

      // --- build user map ---
      const byId = new Map(
        users.map((u) => [String(u?._id), u]).filter(([id]) => !!id),
      );

      const getUserById = (id) => (id ? byId.get(String(id)) : null);

      const getParentUserOf = (child) => {
        const cb = child?.created_by;
        const pid = typeof cb === "object" ? cb?._id : cb;
        if (!pid) return null;
        return getUserById(pid);
      };

      // --- build role meta from CURRENT hierarchy ---
      // roleId -> { deptId, pos }
      const roleMeta = new Map();

      deptArr.forEach((d) => {
        const deptId = String(d?._id || "");
        (d?.roles || []).forEach((r) => {
          const roleId = String(r?.roleId?._id || "");
          const pos = Number(r?.rolePosition);
          if (!roleId || !Number.isFinite(pos)) return;
          roleMeta.set(roleId, { deptId, pos });
        });
      });

      const getRoleId = (u) => String(getUserRoleId(u) || "");
      const getMeta = (u) => roleMeta.get(getRoleId(u)) || null;

      // climb up chain starting from a user (parent) and return list [parent, parentParent, ...]
      const ancestorChain = (startUser) => {
        const out = [];
        const seen = new Set();
        let cur = startUser;
        while (cur && cur?._id && !seen.has(String(cur._id))) {
          out.push(cur);
          seen.add(String(cur._id));
          cur = getParentUserOf(cur);
        }
        return out;
      };

      // determine if current parent is valid under NEW hierarchy for this child
      const isInvalidParent = (child, parent) => {
        const cm = getMeta(child);
        if (!cm) return false;

        // Do NOT auto-fix FIRST position
        if (cm.pos === 1) return false;

        const pm = getMeta(parent);
        if (!pm) return true;
        if (pm.deptId !== cm.deptId) return true;
        if (pm.pos >= cm.pos) return true;
        return false;
      };

      // nearest valid higher-level ancestor role present in new hierarchy
      const pickNewParent = (child, currentParent) => {
        const cm = getMeta(child);
        if (!cm) return null;

        const chain = ancestorChain(currentParent);

        let best = null;
        let bestPos = -999999;

        chain.forEach((u) => {
          const um = getMeta(u);
          if (!um) return;
          if (um.deptId !== cm.deptId) return;
          if (um.pos < cm.pos && um.pos > bestPos) {
            best = u;
            bestPos = um.pos;
          }
        });

        return best;
      };

      const fixes = [];

      users.forEach((child) => {
        if (!isActiveUser(child)) return;
        if (!child?.created_by) return;

        const cm = getMeta(child);
        if (!cm) return;

        // Do NOT auto-fix FIRST position
        if (cm.pos === 1) return;

        const parent = getParentUserOf(child);
        if (!parent) return;

        if (!isInvalidParent(child, parent)) return;

        const newParent = pickNewParent(child, parent);
        const curParentId = String(parent?._id || "");
        const newParentId = newParent?._id ? String(newParent._id) : "";

        if (newParentId && newParentId === curParentId) return;
        if (newParentId && newParentId === String(child?._id)) return;

        fixes.push({
          childId: String(child?._id),
          newParentId: newParentId || null,
        });
      });

      if (!fixes.length) return 0;

      // Step 1: unlink all affected children
      const uniqChildIds = Array.from(new Set(fixes.map((f) => f.childId)));
      await _PostSave(User_UnLink_User, {
        childs: uniqChildIds.map((id) => ({ id })),
      });

      // Step 2: assign to the selected new parents
      const grouped = new Map(); // parentId -> Set(childId)
      fixes.forEach((f) => {
        if (!f.newParentId) return;
        if (!grouped.has(f.newParentId)) grouped.set(f.newParentId, new Set());
        grouped.get(f.newParentId).add(f.childId);
      });

      for (const [parentId, setIds] of grouped.entries()) {
        const ids = Array.from(setIds);
        if (!ids.length) continue;
        await _PostSave(User_Assign_User, {
          parentId,
          childs: ids.map((id) => ({ id })),
        });
      }

      return fixes.length;
    } catch (err) {
      console.error("[AssignTeamMember] auto-fix hierarchy failed:", err);
      return 0;
    }
  }

  /* ==========================================================
     ✅ NEW: Department hierarchy table (NO HARDCODE)
     - Uses selected department roles (rolePosition order)
     - Columns: Root + each role in dept hierarchy + CUSTOMER + Link/Unlink
     - If chain missing at a level => dropdown appears in THAT role cell
       (only for the FIRST missing level above the lowest filled level)
     - Link will apply selected dropdown(s) to fix chain (customer->lowestRole, then lowerRole->higherRole)
     - Unlink only unlinks CUSTOMER (as per your link/unlink column behavior)
  ========================================================== */

  // Reuse the O(1) lookup created above.
  const byId = userById;

  const getById = (id) => (id ? byId.get(String(id)) : null);

  const getParentOf = (u) => {
    const cb = u?.created_by;
    const pid = typeof cb === "object" ? cb?._id : cb;
    return pid ? getById(pid) : null;
  };

  // department roles sorted by rolePosition (1 top ... max bottom)
  const deptRolesSorted = useMemo(() => {
    const roles = Array.isArray(selectedDeptRoles) ? selectedDeptRoles : [];
    // each role item has: rolePosition, roleName, roleId?._id
    return [...roles]
      .filter((r) => r?.roleId?._id && Number.isFinite(Number(r?.rolePosition)))
      .sort((a, b) => Number(a.rolePosition) - Number(b.rolePosition));
  }, [selectedDeptRoles]);

  const deptRoleIdsSet = useMemo(() => {
    const s = new Set();
    deptRolesSorted.forEach((r) => s.add(String(r.roleId._id)));
    return s;
  }, [deptRolesSorted]);

  // map roleId -> role meta
  const roleMeta = useMemo(() => {
    const m = new Map();
    deptRolesSorted.forEach((r) => {
      m.set(String(r.roleId._id), {
        roleId: String(r.roleId._id),
        roleName: r?.roleName || "",
        rolePosition: Number(r.rolePosition),
      });
    });
    return m;
  }, [deptRolesSorted]);

  // users grouped by roleId (for dropdown options)
  const roleUsersMap = useMemo(() => {
    const m = new Map(); // roleId -> users[]
    deptRolesSorted.forEach((r) => m.set(String(r.roleId._id), []));

    // only active users
    (activeUsers || []).forEach((u) => {
      const rid = String(getUserRoleId(u) || "");
      if (!rid) return;
      if (!m.has(rid)) return;
      m.get(rid).push(u);
    });

    // sort options by name
    for (const [rid, list] of m.entries()) {
      list.sort((a, b) => displayName(a).localeCompare(displayName(b)));
      m.set(rid, list);
    }
    return m;
  }, [activeUsers, deptRolesSorted]);

  const selectedDeptName = useMemo(() => {
    if (!selectedDeptId || selectedDeptId === "0") return "";
    if (selectedDeptId === "All_Dept_Heads") return "All Department Head";
    const d = departments.find(
      (x) => String(x?._id) === String(selectedDeptId),
    );
    return d?.departmentName?.departmentName || "";
  }, [selectedDeptId, departments]);

  const showHierarchyTable = useMemo(() => {
    return (
      !!selectedDeptId && selectedDeptId !== "0" && deptRolesSorted.length > 0
    );
  }, [selectedDeptId, deptRolesSorted]);

  // compute chain positions for a customer: rolePosition -> user (only within selected dept roles)
  const chainForCustomer = (cust) => {
    const filled = new Map(); // rolePosition -> user
    const filledByRoleId = new Map(); // roleId -> user

    let cur = getParentOf(cust);
    const seen = new Set();

    while (cur && cur?._id && !seen.has(String(cur._id))) {
      seen.add(String(cur._id));
      const rid = String(getUserRoleId(cur) || "");
      if (deptRoleIdsSet.has(rid)) {
        const meta = roleMeta.get(rid);
        if (meta) {
          filled.set(meta.rolePosition, cur);
          filledByRoleId.set(rid, cur);
        }
      }
      cur = getParentOf(cur);
    }

    // also return immediate parent (could be outside dept; used for assigned status)
    const directParent = getParentOf(cust);

    return { filled, filledByRoleId, directParent };
  };

  const customersForHierarchy = useMemo(() => {
    if (!showHierarchyTable) return [];

    const customers = activeUsers.filter((u) => isCustomerUser(u));
    const q = norm(hierSearch);

    const filtered = !q
      ? customers
      : customers.filter((u) => {
          const nm = norm(displayName(u));
          const pin = String(u?.pincode || "");
          const st = norm(u?.State);
          const dist = norm(u?.District);
          const addr = norm(u?.address);
          return (
            nm.includes(q) ||
            pin.includes(q) ||
            st.includes(q) ||
            dist.includes(q) ||
            addr.includes(q)
          );
        });

    // newest-like order
    return filtered.reverse();
  }, [showHierarchyTable, activeUsers, hierSearch]);

  // For each customer row, decide which ONE role cell should show dropdown:
  // "the role above the role of the name will have a dropdown"
  // Implementation: we show dropdown at the FIRST missing role when scanning bottom->top,
  // but only if the level below exists/known.
  const computeDropdownRoleIdForRow = (cust, chainFilledMap) => {
    if (!deptRolesSorted.length) return null;

    // bottom-most dept role = max rolePosition
    const bottomRole = deptRolesSorted[deptRolesSorted.length - 1];
    const topRole = deptRolesSorted[0];

    // if customer not linked to anyone in dept chain -> dropdown at bottom role
    const bottomPos = Number(bottomRole.rolePosition);
    const hasBottom = !!chainFilledMap.get(bottomPos);

    if (!hasParentAssigned(cust)) {
      // customer has no parent at all => dropdown at bottom role (assign customer to bottom role user)
      return String(bottomRole.roleId._id);
    }

    // customer has some parent; decide the first missing "above" existing nodes:
    // scan from bottom->top:
    for (let i = deptRolesSorted.length - 1; i >= 0; i--) {
      const curRole = deptRolesSorted[i]; // current level
      const curPos = Number(curRole.rolePosition);
      const curHas = !!chainFilledMap.get(curPos);

      if (!curHas) {
        // only show dropdown if the level below exists (or we are at bottom)
        if (i === deptRolesSorted.length - 1) {
          return String(curRole.roleId._id); // bottom missing
        }
        const belowRole = deptRolesSorted[i + 1];
        const belowPos = Number(belowRole.rolePosition);
        const belowHas = !!chainFilledMap.get(belowPos);
        if (belowHas) return String(curRole.roleId._id);
      }
    }

    // everything filled
    // dropdown not needed
    // (also if top missing but no below? this case won't happen)
    void topRole;
    return null;
  };

  const setRowPickValue = (customerId, roleId, userId) => {
    setRowPick((prev) => ({
      ...prev,
      [customerId]: {
        ...(prev[customerId] || {}),
        [roleId]: userId,
      },
    }));
  };

  const toggleRowLink = (customerId, checked) => {
    setRowLink((prev) => {
      const next = new Set(prev);
      if (checked) next.add(customerId);
      else next.delete(customerId);
      return next;
    });
    // mutually exclusive
    setRowUnlink((prev) => {
      const next = new Set(prev);
      next.delete(customerId);
      return next;
    });
  };

  const toggleRowUnlink = (customerId, checked) => {
    setRowUnlink((prev) => {
      const next = new Set(prev);
      if (checked) next.add(customerId);
      else next.delete(customerId);
      return next;
    });
    // mutually exclusive
    setRowLink((prev) => {
      const next = new Set(prev);
      next.delete(customerId);
      return next;
    });
  };

  // Link logic (no hardcode):
  // For each linked customer row:
  // - Determine missing level dropdown roleId (only one shown)
  // - Use rowPick[custId][roleId] to get selected user
  // - If missing is bottom role => assign customer -> selectedUser
  // - Else missing is some upper role => assign lower role user -> selected upper user
  // Additionally, if user picked multiple dropdowns by changing other cells (we allow it),
  // we’ll apply them bottom-up safely.
  const handleApplyHierarchyChanges = async () => {
    try {
      if (working || autoWorking) return;

      const linkIds = Array.from(rowLink);
      const unlinkIds = Array.from(rowUnlink);

      if (linkIds.length === 0 && unlinkIds.length === 0) {
        swal(
          "Nothing Selected",
          "Please select Link or Unlink on rows.",
          "info",
        );
        return;
      }

      setWorking(true);

      // 1) UNLINK customers (bulk)
      if (unlinkIds.length) {
        await _PostSave(User_UnLink_User, {
          childs: unlinkIds.map((id) => ({ id })),
        });
      }

      // 2) LINK: build assignment operations bottom-up
      // We will create assignments as { parentId, childId } and then group by parentId.
      const ops = [];

      for (const custId of linkIds) {
        const cust = customersForHierarchy.find(
          (c) => String(c?._id) === String(custId),
        );
        if (!cust?._id) continue;

        // Build chain using CURRENT data (might be stale vs UI, but ok; we refresh after apply)
        const { filled } = chainForCustomer(cust);

        // Apply picks bottom-up (from bottom-most role to top-most)
        // For each role level that is missing and has a pick:
        // - If it's bottom role: child is CUSTOMER
        // - Else child is the nearest lower filled role user
        for (let i = deptRolesSorted.length - 1; i >= 0; i--) {
          const role = deptRolesSorted[i];
          const roleId = String(role?.roleId?._id || "");
          const pos = Number(role?.rolePosition);

          const pickedUserId = rowPick?.[custId]?.[roleId];
          if (!pickedUserId) continue;

          const alreadyHas = !!filled.get(pos);
          if (alreadyHas) continue;

          // determine child for this assignment
          if (i === deptRolesSorted.length - 1) {
            // bottom role missing => CUSTOMER -> pickedUser
            ops.push({
              parentId: String(pickedUserId),
              childId: String(custId),
            });
            // after assigning, we can consider filled for ordering (best effort):
            // not mutating filled map here; we apply ops grouped later
          } else {
            // upper role missing => lower level user -> picked upper user
            const lowerRole = deptRolesSorted[i + 1];
            const lowerPos = Number(lowerRole.rolePosition);
            const lowerUser = filled.get(lowerPos);
            if (lowerUser?._id) {
              ops.push({
                parentId: String(pickedUserId),
                childId: String(lowerUser._id),
              });
            }
          }
        }

        // Validate: at least one op must exist for this customer
        // If not, warn later (we’ll handle after loop)
      }

      // Validate ops: if link selected but no ops due to missing picks => warn
      const linkNeedingPick = [];
      for (const custId of linkIds) {
        const cust = customersForHierarchy.find(
          (c) => String(c?._id) === String(custId),
        );
        if (!cust?._id) continue;

        const { filled } = chainForCustomer(cust);
        const dropRoleId = computeDropdownRoleIdForRow(cust, filled);
        if (!dropRoleId) continue;

        const picked = rowPick?.[custId]?.[dropRoleId];
        const isAlreadyFull = false;
        if (!picked && !isAlreadyFull) linkNeedingPick.push(custId);
      }

      if (linkNeedingPick.length) {
        setWorking(false);
        swal(
          "Select Name",
          "For Link rows, please select the missing hierarchy user from the dropdown shown in that row.",
          "warning",
        );
        return;
      }

      if (ops.length) {
        const grouped = new Map(); // parentId -> Set(childId)
        ops.forEach((o) => {
          if (!o.parentId || !o.childId) return;
          if (!grouped.has(o.parentId)) grouped.set(o.parentId, new Set());
          grouped.get(o.parentId).add(o.childId);
        });

        for (const [parentId, childSet] of grouped.entries()) {
          const ids = Array.from(childSet);
          if (!ids.length) continue;
          await _PostSave(User_Assign_User, {
            parentId,
            childs: ids.map((id) => ({ id })),
          });
        }
      }

      swal("Success", "Changes applied successfully.", "success");
      await refreshUsers();

      // reset selections after save
      setRowPick({});
      setRowLink(new Set());
      setRowUnlink(new Set());
    } catch (err) {
      console.error(err);
      swal("Error", "Failed to apply changes.", "error");
    } finally {
      setWorking(false);
    }
  };

  // ------- Render -------
  if (loading) {
    return (
      <div
        className="d-flex align-items-center justify-content-center"
        style={{ minHeight: 240 }}
      >
        <Spinner />
      </div>
    );
  }

  return (
    <div>
      <Card>
        <CardBody>
          <Row>
            <Col>
              <h1>Assign Team To Parent Node</h1>
            </Col>
            <Col lg="2" md="2">
              <div className="float-right">
                <Button
                  style={{ cursor: "pointer" }}
                  className="float-right mr-1"
                  color="primary"
                  onClick={() => navigate(-1)}
                >
                  Back
                </Button>
              </div>
            </Col>
          </Row>

          <div className="m-2" />

          {/* Department & Parent Role Selection */}
          <div className="m-2">
            <Row>
              <Col lg="3" md="4" sm="12">
                <Label>Select Department</Label>
                <Input
                  type="select"
                  value={selectedDeptId}
                  onChange={(e) => handleSelectDept(e.target.value)}
                >
                  <option value="0">--Select Department--</option>
                  <option value="All_Dept_Heads">All Department Head</option>
                  {departments.map((d) => (
                    <option key={d?._id} value={d?._id}>
                      {d?.departmentName?.departmentName}
                    </option>
                  ))}
                </Input>
              </Col>

              {showParentUI && selectedDeptId !== "All_Dept_Heads" && (
                <Col lg="3" md="4" sm="12">
                  <Label>Select Parent Role</Label>
                  <Input
                    type="select"
                    value={selectedParentRoleOpt}
                    onChange={handleParentRoleChange}
                  >
                    <option value="NA">--Select Role--</option>
                    {selectedDeptRoles.map((r) => (
                      <option
                        key={r?._id}
                        value={r?._id}
                        data-name={`${r?.rolePosition} ${r?.roleId?._id} ${r?.roleName}`}
                      >
                        {r?.roleName} (Position-{r?.rolePosition})
                      </option>
                    ))}
                  </Input>
                </Col>
              )}

              {/* Parent list (KEEP, do not remove) */}
              {parentUsers.length > 0 && (
                <Col lg="6" md="12" sm="12">
                  <Row>
                    <Col lg="6" md="6" sm="12">
                      <Label>
                        {parentRoleName
                          ? `${parentRoleName} (Parent) List`
                          : "Parent Users"}
                      </Label>
                    </Col>
                    <Col lg="6" md="6" sm="12">
                      <Input
                        type="text"
                        placeholder="Search parent..."
                        value={parentSearch}
                        onChange={(e) => setParentSearch(e.target.value)}
                      />
                    </Col>
                  </Row>

                  <div
                    className="p-1"
                    style={{ borderRight: "1px solid #eee" }}
                  >
                    <Table
                      className="table_heading"
                      responsive
                      style={{ cursor: "pointer" }}
                    >
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Assigned To</th>
                          <th>Name</th>
                          {isSalesPersonRole(getRoleName(parentRowUser)) ? (
                            <>
                              <th>Area</th>
                              <th>Designation</th>
                            </>
                          ) : (
                            <th>Designation</th>
                          )}
                        </tr>
                      </thead>

                      <tbody>
                        {parentRowUser ? (
                          <tr key={parentRowUser?._id || "parentRow"}>
                            <th scope="row">
                              <input
                                type="radio"
                                name="selectedParent"
                                onChange={() =>
                                  setSelectedParentUser(parentRowUser)
                                }
                                checked={
                                  selectedParentUser?._id === parentRowUser?._id
                                }
                              />
                            </th>

                            <td>
                              {(() => {
                                const cb = getCreatedByUser(
                                  parentRowUser?.created_by,
                                  userById,
                                );
                                return cb ? (
                                  <strong style={{ fontSize: 16 }}>
                                    {displayName(cb)}
                                  </strong>
                                ) : (
                                  <Badge color="primary">
                                    <strong>Root</strong>
                                  </Badge>
                                );
                              })()}
                            </td>

                            <td>
                              <Input
                                type="select"
                                value={
                                  parentNameDropdownValue ||
                                  String(parentRowUser?._id || "")
                                }
                                onChange={handlePickParentUser}
                              >
                                <option value="">Select Name</option>
                                {filteredParentUsers.map((p) => (
                                  <option key={p?._id} value={p?._id}>
                                    {displayName(p)}
                                  </option>
                                ))}
                              </Input>
                            </td>

                            {isSalesPersonRole(getRoleName(parentRowUser)) ? (
                              <>
                                <td>
                                  {parentRowUser?.area ||
                                    parentRowUser?.sId ||
                                    "-"}
                                </td>
                                <td>{parentRowUser?.rolename?.roleName}</td>
                              </>
                            ) : (
                              <td>{parentRowUser?.rolename?.roleName}</td>
                            )}
                          </tr>
                        ) : null}
                      </tbody>
                    </Table>
                  </div>
                </Col>
              )}
            </Row>
          </div>

          <hr />

          <div className="p-2">
            {childUsers.length > 0 ? (
              <Row>
                <Col lg="12" md="12" sm="12">
                  <Row className="align-items-end">
                    <Col lg="4" md="6" sm="12">
                      <h5 className="mb-2">
                        <strong>
                          {selectedDeptId === "All_Dept_Heads"
                            ? "All Dept Head Users"
                            : partyMode
                              ? "Party Users"
                              : childRoleCandidates?.[0]?.roleName
                                ? `${childRoleCandidates[0].roleName} (child) Users`
                                : "Child Users"}
                        </strong>
                      </h5>
                    </Col>

                    <Col lg="5" md="6" sm="12" className="mb-2">
                      <Button
                        color="success"
                        className="mr-2"
                        disabled={autoWorking || working}
                        onClick={handleAutoAssign}
                      >
                        {autoWorking ? "Auto Assigning..." : "Auto Assign"}
                      </Button>

                      {selectedChildren.size > 0 && (
                        <Button
                          color="primary"
                          disabled={working}
                          onClick={handleAssign}
                          className="mr-2"
                        >
                          {working ? "Assigning..." : "Submit"}
                        </Button>
                      )}

                      {selectedChildrenForUnlink.size > 0 && (
                        <Button
                          color="danger"
                          disabled={working}
                          onClick={handleUnlink}
                        >
                          {working ? "Unlinking..." : "UnLink"}
                        </Button>
                      )}
                    </Col>

                    <Col lg="3" md="12" sm="12" className="mb-2">
                      <Input
                        type="text"
                        placeholder="Search children..."
                        value={childSearch}
                        onChange={(e) => setChildSearch(e.target.value)}
                      />
                    </Col>
                  </Row>

                  <div className="p-1">
                    <Table
                      className="table_heading"
                      responsive
                      style={{ cursor: "pointer" }}
                    >
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Assigned To</th>
                          <th>Name</th>
                          {partyMode ? <th>Address</th> : <th>Designation</th>}
                          <th>PinCode</th>
                          <th>UnLink Child</th>
                        </tr>
                      </thead>

                      <tbody>
                        {visibleChildUsers.map((u) => {
                          const createdByUser = getCreatedByUser(
                            u?.created_by,
                            userById,
                          );
                          const alreadyAssigned = !!createdByUser;
                          const isCustomer = isCustomerUser(u);

                          return (
                            <tr key={u?._id}>
                              <th scope="row">
                                {!alreadyAssigned && (
                                  <Input
                                    title="Link to Parent"
                                    type="checkbox"
                                    style={{ position: "unset", margin: 0 }}
                                    checked={selectedChildren.has(u?._id)}
                                    onChange={(e) =>
                                      toggleChildSelect(u, e.target.checked)
                                    }
                                  />
                                )}
                              </th>

                              <td>
                                {alreadyAssigned ? (
                                  <strong style={{ fontSize: 16 }}>
                                    {displayName(createdByUser)}
                                  </strong>
                                ) : (
                                  <Badge color="primary">
                                    <strong>Not Assigned</strong>
                                  </Badge>
                                )}
                              </td>

                              <td>{displayName(u)}</td>

                              {isCustomer ? (
                                <td>{displayAddress(u)}</td>
                              ) : (
                                <td>{u?.rolename?.roleName || "-"}</td>
                              )}

                              <td>
                                <strong>{u?.pincode || "-"}</strong>
                              </td>

                              <td>
                                {alreadyAssigned && (
                                  <Input
                                    title="UnLink from Parent"
                                    type="checkbox"
                                    style={{ position: "unset", margin: 0 }}
                                    checked={selectedChildrenForUnlink.has(
                                      u?._id,
                                    )}
                                    onChange={(e) =>
                                      toggleChildUnlink(u, e.target.checked)
                                    }
                                  />
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>

                    <div className="d-flex align-items-center justify-content-between flex-wrap mt-2">
                      <small className="text-muted mb-2">
                        Showing{" "}
                        {Math.min(visibleChildCount, filteredChildUsers.length)}{" "}
                        of {filteredChildUsers.length} record(s)
                      </small>

                      {visibleChildCount < filteredChildUsers.length && (
                        <Button
                          size="sm"
                          color="secondary"
                          outline
                          className="mb-2"
                          onClick={() =>
                            setVisibleChildCount((count) =>
                              Math.min(
                                count + CHILD_RENDER_BATCH,
                                filteredChildUsers.length,
                              ),
                            )
                          }
                        >
                          Load next{" "}
                          {Math.min(
                            CHILD_RENDER_BATCH,
                            filteredChildUsers.length - visibleChildCount,
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                </Col>
              </Row>
            ) : (
              <>
                {noNextChild ? (
                  <Row>
                    <Col lg="4" md="6">
                      <Label style={{ color: "red" }}>
                        No Next Child Found
                      </Label>
                      <Input readOnly value="No Child Found" />
                    </Col>
                  </Row>
                ) : null}
              </>
            )}
          </div>
        </CardBody>
      </Card>

      {/* ===========================
          AUTO ASSIGN CONFLICT DIALOG
          =========================== */}
      <Modal
        isOpen={autoModalOpen}
        toggle={() => !autoWorking && setAutoModalOpen(false)}
        size="lg"
      >
        <ModalHeader toggle={() => !autoWorking && setAutoModalOpen(false)}>
          Resolve Multiple Sales Persons for Same Pincode
        </ModalHeader>

        <ModalBody>
          {autoRows.length === 0 ? (
            <div>No conflicts to resolve.</div>
          ) : (
            <Table responsive className="table_heading">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Customer</th>
                  <th>Pincode</th>
                  <th>Sales Person</th>
                </tr>
              </thead>
              <tbody>
                {autoRows.map((row, idx) => (
                  <tr key={row.customerId}>
                    <td>{idx + 1}</td>
                    <td>{row.customerName}</td>
                    <td>{row.pincode || "-"}</td>
                    <td>
                      <Input
                        type="select"
                        value={row.selectedSalesId}
                        disabled={autoWorking}
                        onChange={(e) =>
                          handleAutoRowSalesChange(idx, e.target.value)
                        }
                      >
                        <option value="">-- Select Sales Person --</option>
                        {row.options.map((sp) => (
                          <option key={sp?._id} value={sp?._id}>
                            {displayName(sp)}
                          </option>
                        ))}
                      </Input>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </ModalBody>

        <ModalFooter>
          <Button
            color="primary"
            onClick={handleAutoModalSubmit}
            disabled={autoWorking || autoRows.length === 0}
          >
            {autoWorking ? "Assigning..." : "Submit"}
          </Button>
          <Button
            color="secondary"
            onClick={() => !autoWorking && setAutoModalOpen(false)}
            disabled={autoWorking}
          >
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default AssignTeamMember;
